// Normalises the booked/selected fare product to the values agreed in the
// marketing data-layer spec:
//   train_class: 'standard' | 'premium'
//   fare_type:   'promo' | 'flex' | 'flex_plus'
// Everything is read from the product itself (configured in the back-office
// product admin), so new or renamed products need no code change.

import { ServiceClassTypeEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";

export type TrainClass = "standard" | "premium";
export type FareType = "promo" | "flex" | "flex_plus";

/** The product fields this needs; offer products and booked products both have them. */
export interface FareProductRef {
  code?: string;
  name?: string;
  /** OSDM serviceClass.type (ServiceClassTypeEnum; env-configurable). */
  serviceClassType?: string;
  productTags?: string[];
}

const FARE_TYPES: readonly FareType[] = ["promo", "flex", "flex_plus"];
const FARE_TYPE_TAG = "FARE_TYPE=";

const trainClassOf = (raw: unknown): TrainClass | "" => {
  const lower = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (!lower) return "";
  // The configured service-class codes first, then plain-word hints.
  if (lower === ServiceClassTypeEnum.HIGH.toLowerCase() || lower.includes("premium")) return "premium";
  if (lower === ServiceClassTypeEnum.STANDARD.toLowerCase() || lower.includes("standard")) return "standard";
  return "";
};

const fareTypeOf = (product?: FareProductRef): FareType | "" => {
  // 1. Explicit back-office tag, e.g. FARE_TYPE=flex_plus.
  const tag = (product?.productTags ?? [])
    .map((t) => String(t).trim())
    .find((t) => t.toUpperCase().startsWith(FARE_TYPE_TAG));
  const tagged = tag?.slice(FARE_TYPE_TAG.length).trim().toLowerCase() as FareType | undefined;
  if (tagged && FARE_TYPES.includes(tagged)) return tagged;

  // 2. Tier word in the product name (prod: "<Comfort|Premium> <Saver|Value|Flex>").
  // ponytail: tier -> fare_type pairing pending ERM confirmation; the tag
  // above overrides it per product without a release.
  const name = (product?.name ?? "").toLowerCase();
  if (/\bsaver\b|\bpromo\b/.test(name)) return "promo";
  if (/\bvalue\b/.test(name)) return "flex";
  if (/\bflex\b/.test(name)) return "flex_plus";
  return "";
};

/**
 * train_class from the product's service class (falling back to `classHint`,
 * e.g. the selected price class "standardDuo"), fare_type from the product.
 */
export function fareParamsOf(
  product?: FareProductRef,
  classHint?: string,
): { train_class: TrainClass | ""; fare_type: FareType | "" } {
  return {
    train_class: trainClassOf(product?.serviceClassType) || trainClassOf(classHint),
    fare_type: fareTypeOf(product),
  };
}
